/**
 * Block Icon Component
 * Renders SVG icons for email builder block types
 */

interface BlockIconProps {
  iconName: string;
  className?: string;
}

export function BlockIcon({ iconName, className = "h-4 w-4" }: BlockIconProps) {
  // Normalize icon name to handle both lowercase and PascalCase
  const normalizedName = iconName.toLowerCase().replace(/-/g, '');

  const icons: Record<string, JSX.Element> = {
    // Text — letter "T"
    text: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 7V4h14v3M12 4v16m-3 0h6" />
      </svg>
    ),
    // Heading — letter "H"
    heading: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 4v16M18 4v16M6 12h12" />
      </svg>
    ),
    // Image — landscape photo
    image: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    // Button — rounded pill with a label line
    button: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <rect x="3" y="8" width="18" height="8" rx="4" strokeWidth={2} />
        <path strokeLinecap="round" strokeWidth={2} d="M9 12h6" />
      </svg>
    ),
    // Divider — horizontal rule between content
    divider: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12h16M8 6h8M8 18h8" />
      </svg>
    ),
    // Spacer — vertical double arrow between two edges
    spacer: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16M4 20h16M12 8v8m0-8l-2.5 2.5M12 8l2.5 2.5M12 16l-2.5-2.5m2.5 2.5l2.5-2.5" />
      </svg>
    ),
    // Section — wide container with content row
    section: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <rect x="3" y="5" width="18" height="14" rx="2" strokeWidth={2} />
        <path strokeLinecap="round" strokeWidth={2} d="M7 12h10" />
      </svg>
    ),
    // Layout grid — four panels
    layout: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
      </svg>
    ),
    // Columns — side-by-side panels
    columns: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4H5a2 2 0 00-2 2v12a2 2 0 002 2h4m0-16v16m0-16h10a2 2 0 012 2v12a2 2 0 01-2 2H9" />
      </svg>
    ),
    // Group — dashed container holding two blocks
    group: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <rect x="3" y="5" width="18" height="14" rx="2" strokeWidth={2} strokeDasharray="3 3" />
        <rect x="7" y="9" width="4" height="6" rx="1" strokeWidth={2} />
        <rect x="13" y="9" width="4" height="6" rx="1" strokeWidth={2} />
      </svg>
    ),
    // Hero — banner with headline and CTA pill
    hero: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <rect x="3" y="4" width="18" height="16" rx="2" strokeWidth={2} />
        <path strokeLinecap="round" strokeWidth={2} d="M7 9h10" />
        <rect x="8.5" y="13" width="7" height="3.5" rx="1.75" strokeWidth={1.5} />
      </svg>
    ),
    // Footer — container with emphasized bottom band
    footer: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5zM3 16h18M7 19h4" />
      </svg>
    ),
    // Quote — opening quotation marks
    quote: (
      <svg className={className} fill="currentColor" viewBox="0 0 24 24" stroke="none">
        <path d="M10 7c-3 0-5 2.2-5 5.1 0 2.4 1.7 4 3.8 4 1.8 0 3.2-1.3 3.2-3.1 0-1.7-1.2-2.9-2.8-2.9-.3 0-.6 0-.8.1C8.8 9 9.8 8.2 11 8l-1-1zM19 7c-3 0-5 2.2-5 5.1 0 2.4 1.7 4 3.8 4 1.8 0 3.2-1.3 3.2-3.1 0-1.7-1.2-2.9-2.8-2.9-.3 0-.6 0-.8.1C17.8 9 18.8 8.2 20 8l-1-1z" />
      </svg>
    ),
    // Menu — navigation links in a row
    menu: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1v-4zm7 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4zm7 0a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2a1 1 0 01-1-1v-4z" />
      </svg>
    ),
    // HTML — code brackets
    html: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    // Video — play button in a frame
    video: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <rect x="3" y="5" width="18" height="14" rx="2" strokeWidth={2} />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9l5 3-5 3V9z" />
      </svg>
    ),
    // Social — share network
    social: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
      </svg>
    ),
  };

  // Aliases: legacy lucide-style names used in block definitions map onto the
  // same icons as the raw block type names used by the outline panel.
  const aliases: Record<string, string> = {
    type: 'text',
    mousepointer: 'button',
    mousepointerclick: 'button',
    minus: 'divider',
    space: 'spacer',
    movevertical: 'spacer',
    layoutgrid: 'layout',
    star: 'hero',
    code: 'html',
    play: 'video',
    share2: 'social',
    column: 'columns',
  };

  const resolved = icons[normalizedName] ?? icons[aliases[normalizedName]];

  return resolved || (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <rect x="4" y="4" width="16" height="16" rx="2" strokeWidth={2} strokeDasharray="3 3" />
    </svg>
  );
}
